<template>
  <div class="web-camera-container">
    <loader :loading="true" size="sm" v-if="isLoading"></loader>
    <div ref="fullscreenElement" class="camera-viewport">
      <img class="close-cam" src="@/assets/images/icons/dialog-close.svg" @click="closeCamera" v-if="isFullScreen" />
      <button
        v-if="devices.length > 1"
        type="button"
        class="switch-camera-button"
        :aria-label="$t('switch_camera')"
        :title="$t('switch_camera')"
        @click="switchCamera"
      >
        <i class="fa-solid fa-camera-rotate" aria-hidden="true"></i>
      </button>
      <!-- Container to hold CSS scale when native zoom isn't supported -->
      <div class="webcam-wrapper" :style="wrapperStyle">
        <vue-web-cam
          ref="webcam"
          :device-id="deviceId"
          :height="'100%'"
          :width="'100%'"
          @started="onStarted"
          @stopped="onStopped"
          @error="onError"
          @cameras="onCameras"
          @camera-change="onCameraChange"
        />
      </div>

      <input id="gf-uploader" type="file" ref="ga-uploader" :accept="'.jpg, .jpeg, .png, .heic'" @change="onFileChange" class="d-none" />
      <img src="@/assets/images/icons/photo-gallery-icon.svg" class="gallery-icon" @click="showGallery" />
      <img
        src="@/assets/images/icons/camera-capture.svg"
        class="capture-img"
        :class="[isPhotoTaken ? 'img-taken' : null]"
        @click="captureImage"
      />
      <!-- Zoom Slider (Works on both Native and CSS fallback) -->
      <div class="zoom-controls">
        <label for="zoom">Camera Zoom: {{ currentZoom }}x</label>
        <input
          id="zoom"
          type="range"
          :min="zoomMin"
          :max="zoomMax"
          :step="zoomStep"
          v-model.number="currentZoom"
          @input="handleZoomChange"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import { WebCam } from "vue-web-cam";
import "@fortawesome/fontawesome-free/css/solid.min.css";
import $ from "jquery";
import { isReactNative, postToRNVebView } from "@/helpers/reactNativeHelper";
Vue.use(WebCam);

export default {
  name: "Camera",
  props: ["show"],
  components: {
    "vue-web-cam": WebCam,
  },
  data() {
    return {
      img: null,
      camera: null,
      deviceId: null,
      devices: [],
      isFullScreen: false,
      screenWidth: 0,
      screenHeight: 0,
      isCameraOpen: false,
      isPhotoTaken: false,
      isShotPhoto: false,
      isLoading: false,
      currentCam: null,
      fileObject: undefined,
      cameraTrack: null,
      supportsZoom: false,
      currentZoom: 1,
      zoomMin: 1,
      zoomMax: 3,
      zoomStep: 0.1,
      supportsNativeZoom: false,
    };
  },
  computed: {
    currentDevice: function () {
      return this.devices.find((n) => n.deviceId === this.deviceId);
    },
    // Generates CSS scale if native hardware zoom is unavailable
    wrapperStyle() {
      if (!this.supportsNativeZoom && this.currentZoom > 1) {
        return {
          transform: `scale(${this.currentZoom})`,
          transformOrigin: "center center",
          transition: "transform 0.1s ease-out",
        };
      }
      return {};
    },
  },
  watch: {
    camera: function (id) {
      this.deviceId = id;
    },
    show: function (newVal) {
      this.isPhotoTaken = !newVal;
    },
    isFullScreen: function (newVal) {
      if (!newVal) {
        this.closeCamera();
      }
    },
  },
  mounted() {
    this.screenWidth = window.innerWidth;
    this.screenHeight = window.innerHeight;
    window.addEventListener("message", this.handleWebViewMessage);
  },
  beforeDestroy() {
    window.removeEventListener("message", this.handleWebViewMessage);
  },
  methods: {
    handleWebViewMessage(event) {
      if (!isReactNative()) return;
      const data = JSON.parse(event.data);
      if (data.type === "permission_result" && data.permission_type === "camera") {
        if (data.granted) {
          console.log("Camera permission granted, retrying...");
          this.onStart(); // újraindítja a kamerát automatikusan
        }
      }
    },
    toggleCamera() {
      if (this.isCameraOpen) {
        this.isCameraOpen = false;
        this.isPhotoTaken = false;
        this.isShotPhoto = false;
        this.onStop();
      } else {
        this.isCameraOpen = true;
        this.onStart();
      }
    },
    captureImage() {
      if (!this.isPhotoTaken) {
        this.isShotPhoto = true;
        setTimeout(() => {
          this.isShotPhoto = false;
        }, 50);
      }

      this.isPhotoTaken = !this.isPhotoTaken;
      const rawCapturedImage = this.$refs.webcam.capture();

      // If relying on CSS Digital Zoom, crop canvas manually to match screen preview
      if (!this.supportsNativeZoom && this.currentZoom > 1) {
        this.cropZoomedImage(rawCapturedImage, this.currentZoom, (zoomedBase64) => {
          this.img = zoomedBase64;
          this.$emit("onImageCaptured", this.img);
        });
      } else {
        this.img = rawCapturedImage;
        this.$emit("onImageCaptured", this.img);
      }
    },
    onStarted(stream) {
      // Get the video track directly from the media stream
      const videoTracks = stream.getVideoTracks();
      if (videoTracks.length === 0) return;

      this.cameraTrack = videoTracks[0];

      // Check if the camera hardware supports native zoom
      const capabilities = this.cameraTrack.getCapabilities ? this.cameraTrack.getCapabilities() : {};

      console.log("Camera capabilities:", capabilities);
      // Hardware Zoom Check (Android Chrome, Desktop Chrome)
      if ("zoom" in capabilities) {
        this.supportsNativeZoom = true;
        this.zoomMin = capabilities.zoom.min;
        this.zoomMax = capabilities.zoom.max;
        this.zoomStep = capabilities.zoom.step || 0.1;
        this.currentZoom = capabilities.zoom.current || this.zoomMin;
      } else {
        // Fallback to CSS Digital Zoom (iOS Safari, Desktop Safari)
        this.supportsNativeZoom = false;
        this.zoomMin = 1;
        this.zoomMax = 3;
        this.zoomStep = 0.1;
        this.currentZoom = 1;
      }
    },
    async applyZoom() {
      if (!this.cameraTrack || !this.supportsZoom) return;

      try {
        // Apply native constraint to the video track
        await this.cameraTrack.applyConstraints({
          advanced: [{ zoom: parseFloat(this.currentZoom) }],
        });
      } catch (error) {
        console.error("Failed to apply zoom:", error);
      }
    },
    onStopped(stream) {
      this.exitFullScreen();
    },
    onStop() {
      this.$refs.webcam.stop();
    },
    onStart() {
      this.isLoading = true;

      const constraints = (window.constraints = {
        audio: false,
        video: true,
      });

      postToRNVebView("permission", {
        permission_type: "camera",
      });

      navigator.mediaDevices
        .getUserMedia(constraints)
        .then((stream) => {
          this.isLoading = false;
          this.$refs.webcam.start();
          //this.enterFullScreen();
        })
        .catch((error) => {
          this.isLoading = false;
          alert("May the browser didn't support or there is some errors.");
        });
    },
    onError(error) {
      console.log("On Error Event", error);
    },
    onCameras(cameras) {
      this.devices = cameras;
      if (!cameras.length) return;

      let lastSelectedDeviceId = null;
      try {
        lastSelectedDeviceId = window.localStorage.getItem("agrocontrol.lastSelectedCameraDeviceId");
      } catch (error) {
        console.warn("Could not read the last selected camera.", error);
      }

      const selectedDevice = cameras.find((device) => device.deviceId === lastSelectedDeviceId) || cameras[cameras.length - 1];
      this.currentCam = selectedDevice;
      this.deviceId = selectedDevice.deviceId;
      this.camera = selectedDevice.deviceId;
      this.rememberCamera(selectedDevice.deviceId);
    },
    onCameraChange(deviceId) {
      this.deviceId = deviceId;
      this.camera = deviceId;
      this.currentCam = this.devices.find((device) => device.deviceId === deviceId) || null;
      if (this.currentCam) this.rememberCamera(deviceId);
    },
    enterFullScreen() {
      const element = this.$refs.fullscreenElement;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
      this.isFullScreen = true;
    },
    exitFullScreen() {
      if (document.requestExitFullscreen) {
        document.requestExitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
      this.isFullScreen = false;
    },
    closeCamera() {
      this.$emit("onClose");
    },
    downloadImage() {
      const download = document.getElementById("downloadPhoto");
      const canvas = document.getElementById("photoTaken").toDataURL("image/jpeg").replace("image/jpeg", "image/octet-stream");
      download.setAttribute("href", canvas);
    },
    switchCamera() {
      if (this.devices.length < 2) return;

      const currentIndex = this.devices.findIndex((device) => device.deviceId === this.deviceId);
      const nextIndex = currentIndex < 0 ? this.devices.length - 1 : (currentIndex + 1) % this.devices.length;
      const nextDevice = this.devices[nextIndex];
      this.currentCam = nextDevice;
      this.deviceId = nextDevice.deviceId;
      this.camera = nextDevice.deviceId;
      this.rememberCamera(nextDevice.deviceId);
    },
    rememberCamera(deviceId) {
      try {
        window.localStorage.setItem("agrocontrol.lastSelectedCameraDeviceId", deviceId);
      } catch (error) {
        console.warn("Could not remember the selected camera.", error);
      }
    },
    onFileChange(e) {
      let files = e.target.files || e.dataTransfer.files;

      if (!files.length) return;

      this.img = files[0];

      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          const base64data = canvas.toDataURL("image/jpeg");

          this.$emit("onFileLoaded", base64data);
          this.$emit("onImageCaptured", base64data);
        };
      };
      reader.readAsDataURL(this.img);
    },
    showGallery() {
      //open file uploader
      $("#gf-uploader").click();
    },
    async handleZoomChange() {
      if (this.supportsNativeZoom && this.cameraTrack) {
        try {
          await this.cameraTrack.applyConstraints({
            advanced: [{ zoom: parseFloat(this.currentZoom) }],
          });
        } catch (error) {
          console.error("Failed to apply native zoom:", error);
        }
      }
      // If native zoom isn't supported, computed property `wrapperStyle` handles CSS scaling automatically
    },
    // Utility to crop standard captured canvas image when using CSS zoom
    cropZoomedImage(base64Image, zoomLevel, callback) {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const width = img.width;
        const height = img.height;

        const cropWidth = width / zoomLevel;
        const cropHeight = height / zoomLevel;
        const cropX = (width - cropWidth) / 2;
        const cropY = (height - cropHeight) / 2;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, cropX, cropY, cropWidth, cropHeight, 0, 0, width, height);
        callback(canvas.toDataURL("image/jpeg"));
      };
      img.src = base64Image;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/global/global.scss";

.web-camera-container {
  position: relative;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;

  .flex-cont {
    display: flex;
    flex-direction: row;
    background-color: #000000;
  }

  .cam-title {
    width: 100%;
    padding-top: 20px;
    padding-bottom: 16px;
    padding-left: 40px;
    justify-content: center;
    z-index: 99998;
    background-color: #000;
    color: #fff;
    display: flex;
  }

  .gallery-icon {
    filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(53deg) brightness(112%) contrast(101%);
    width: 48px;
    height: 48px;
    position: absolute;
    bottom: 104px;
    left: calc(50% - 92px);
    z-index: 99999;
  }

  .switch-icon {
    filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(53deg) brightness(112%) contrast(101%);
    width: 24px;
    height: 32px;
    margin-right: 18px;
    z-index: 99999;
    display: flex;
    margin-top: 14px;
  }

  @keyframes preload {
    0% {
      opacity: 1;
    }

    50% {
      opacity: 0.4;
    }

    100% {
      opacity: 1;
    }
  }

  .capture-img {
    filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(53deg) brightness(112%) contrast(101%);
    width: 56px;
    height: 56px;
    position: absolute;
    bottom: 100px;
    left: calc(50% - 28px);
    z-index: 99999;
  }

  .img-taken {
    filter: invert(40%) sepia(94%) saturate(427%) hue-rotate(86deg) brightness(91%) contrast(87%) !important;
  }

  .close-cam {
    filter: invert(100%) sepia(100%) saturate(0%) hue-rotate(53deg) brightness(112%) contrast(101%);
    position: absolute;
    top: 16px;
    right: 16px;
    cursor: pointer;
    width: 24px;
    z-index: 99999;
  }
}

video {
  background-color: #000000 !important;
}

.camera-viewport {
  position: relative;
  overflow: hidden; /* Essential to prevent CSS zoom from expanding beyond camera bounds */
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.switch-camera-button {
  position: absolute;
  top: 4px;
  left: 4px;
  z-index: 99999;
  display: flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  cursor: pointer;
  font-size: 18px;
}

.switch-camera-button:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

.webcam-wrapper {
  width: 100%;
  height: 100%;
}

.camera-viewport video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.zoom-controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 99999;
  background: rgba(0, 0, 0, 0.6);
  padding: 8px 16px;
  border-radius: 20px;
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
